import { useCallback, useEffect, useMemo, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import {
  advanceBrebKeyPoll,
  type BrebKeyOption,
  type BrebKeyPollCursor,
  type BrebKeyPollPlan,
  type BrebKeySource,
  type BrebReceivingKey,
  BrebReceivingKeyService,
  type BrebReceivingKeyState,
  brebKeyPollPlan,
  brebKeyPollTier,
  seedCreatedKeyState,
  seedPrimaryKeyState,
  seedRemovedKeyState,
} from "@/shared/services/payments/brebReceivingKey.service";
import { readApiStatus } from "@/shared/utils/apiError";

const STATE_TTL_MS = 30_000;

interface CachedState {
  at: number;
  settled: boolean;
  promise: Promise<BrebReceivingKeyState>;
}

interface ScopePoll {
  id: string;
  timer: ReturnType<typeof setTimeout> | null;
}

const cache = new Map<string, CachedState>();
const lastSeen = new Map<string, string>();
const polls = new Map<string, ScopePoll>();
const listeners = new Map<string, Set<() => void>>();

const notify = (scope: string) => listeners.get(scope)?.forEach((listener) => listener());

// An UNKNOWN is a directory hiccup, not an answer: the next read must ask again.
const hasUnknownOption = (state: BrebReceivingKeyState): boolean =>
  state.options.some((option) => option.availability === "UNKNOWN");

function remember(scope: string, state: BrebReceivingKeyState): void {
  const signature = JSON.stringify(state);
  const previous = lastSeen.get(scope);
  lastSeen.set(scope, signature);
  if (previous !== undefined && previous !== signature) notify(scope);
}

function readState(scope: string, organizationId: string | undefined): Promise<BrebReceivingKeyState> {
  const cached = cache.get(scope);
  if (cached && Date.now() - cached.at < STATE_TTL_MS) return cached.promise;

  const entry: CachedState = {
    at: Date.now(),
    settled: false,
    promise: BrebReceivingKeyService.getState(organizationId),
  };
  cache.set(scope, entry);
  entry.promise.then(
    (state) => {
      entry.settled = true;
      if (hasUnknownOption(state) && cache.get(scope) === entry) cache.delete(scope);
      remember(scope, state);
    },
    () => {
      entry.settled = true;
      if (cache.get(scope) === entry) cache.delete(scope);
    },
  );
  return entry.promise;
}

function seedState(scope: string, state: BrebReceivingKeyState): CachedState {
  const entry: CachedState = { at: Date.now(), settled: true, promise: Promise.resolve(state) };
  cache.set(scope, entry);
  lastSeen.set(scope, JSON.stringify(state));
  notify(scope);
  return entry;
}

// Unlike readState, a failure keeps what is cached: the seeded key must survive a refresh that fails.
function refreshSeed(scope: string, organizationId: string | undefined, seed: CachedState): void {
  BrebReceivingKeyService.getState(organizationId).then(
    (state) => {
      if (cache.get(scope) !== seed) return;
      if (hasUnknownOption(state)) cache.delete(scope);
      else cache.set(scope, { at: Date.now(), settled: true, promise: Promise.resolve(state) });
      remember(scope, state);
    },
    () => undefined,
  );
}

function invalidateScope(scope: string): void {
  const entry = cache.get(scope);
  if (entry && !entry.settled) return;
  cache.delete(scope);
  notify(scope);
}

function stopPoll(scope: string): void {
  const poll = polls.get(scope);
  if (poll?.timer) clearTimeout(poll.timer);
  polls.delete(scope);
}

// The chain schedules itself, so a read that fails does not end the polling.
function syncPoll(scope: string, plan: BrebKeyPollPlan | null): void {
  if (!plan) {
    stopPoll(scope);
    return;
  }
  if (polls.get(scope)?.id === plan.id) return;
  stopPoll(scope);
  const poll: ScopePoll = { id: plan.id, timer: null };
  polls.set(scope, poll);
  const tick = (cursor: BrebKeyPollCursor) => {
    poll.timer = setTimeout(() => {
      poll.timer = null;
      let next: BrebKeyPollCursor | null = cursor;
      // A hidden tab spends no attempts: coming back revalidates anyway.
      if (document.visibilityState !== "hidden") {
        next = advanceBrebKeyPoll(plan, cursor);
        invalidateScope(scope);
      }
      if (next && polls.get(scope) === poll) tick(next);
    }, brebKeyPollTier(plan, cursor).intervalMs);
  };
  tick({ tier: 0, count: 0 });
}

/**
 * After a key changed server-side (a refused payin, a transfer): mounted views read again, cached ones are
 * dropped.
 */
export function refreshBrebReceivingKeys(): void {
  for (const scope of new Set([...listeners.keys(), ...cache.keys()])) invalidateScope(scope);
}

function revalidateStaleScopes(): void {
  if (document.visibilityState === "hidden") return;
  for (const scope of listeners.keys()) {
    const entry = cache.get(scope);
    if (!entry || Date.now() - entry.at >= STATE_TTL_MS) invalidateScope(scope);
  }
}

function onVisibilityChange(): void {
  if (document.visibilityState === "visible") revalidateStaleScopes();
}

function subscribe(scope: string, listener: () => void): () => void {
  if (listeners.size === 0) {
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", revalidateStaleScopes);
  }
  const scoped = listeners.get(scope) ?? new Set<() => void>();
  scoped.add(listener);
  listeners.set(scope, scoped);

  return () => {
    scoped.delete(listener);
    if (scoped.size > 0) return;
    listeners.delete(scope);
    stopPoll(scope);
    if (listeners.size === 0) {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", revalidateStaleScopes);
    }
  };
}

export interface UseBrebReceivingKey {
  state: BrebReceivingKeyState | null;
  isLoading: boolean;
  /** A reload asked for by this instance is in flight; the previous state stays on screen meanwhile. */
  isRefreshing: boolean;
  hasError: boolean;
  /** False for staff with no organization in scope: there is no key to show. */
  isAvailable: boolean;
  /** Undefined for members: they are pinned to their organization server-side and must not send one. */
  scopedOrgId: string | undefined;
  reload: () => void;
  create: (source: BrebKeySource) => Promise<BrebReceivingKey>;
  /** Deletes one key (the primary without `keyId`); the state is then read again. */
  remove: (keyId: string | undefined, acceptLiveQrs?: boolean, twoFactorCode?: string) => Promise<void>;
  setPrimary: (keyId: string) => Promise<BrebReceivingKey>;
  /** Fire-and-forget: tells the Coloca organization holding that option's value that someone asked for it. */
  notifyHolder: (source: BrebKeySource) => Promise<boolean>;
}

export function useBrebReceivingKey(organizationId?: string): UseBrebReceivingKey {
  const { user, isInternalUser } = useAuth();
  const scopedOrgId = isInternalUser
    ? (organizationId ?? user?.organization_id ?? user?.organization?.id ?? undefined)
    : undefined;
  const isAvailable = isInternalUser ? !!scopedOrgId : !!user;
  const scope = `${user?.id ?? "anonymous"}:${scopedOrgId ?? "self"}`;

  const [state, setState] = useState<BrebReceivingKeyState | null>(null);
  const [isLoading, setIsLoading] = useState(isAvailable);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [version, setVersion] = useState(0);

  // Another user signing in on this tab must never see the previous user's key, not even for one render.
  const [renderedScope, setRenderedScope] = useState(scope);
  if (renderedScope !== scope) {
    setRenderedScope(scope);
    setState(null);
    setHasError(false);
    setIsLoading(isAvailable);
  }

  useEffect(() => subscribe(scope, () => setVersion((current) => current + 1)), [scope]);

  useEffect(() => {
    if (!isAvailable) {
      /* eslint-disable react-hooks-eslint/set-state-in-effect */
      setState(null);
      setIsLoading(false);
      /* eslint-enable react-hooks-eslint/set-state-in-effect */
      return;
    }
    let cancelled = false;
    readState(scope, scopedOrgId)
      .then((next) => {
        if (cancelled) return;
        setState(next);
        setHasError(false);
      })
      .catch(() => {
        if (!cancelled) setHasError(true);
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
        setIsRefreshing(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAvailable, scope, scopedOrgId, version]);

  const pollPlan = useMemo(() => (state ? brebKeyPollPlan(state.keys) : undefined), [state]);
  useEffect(() => {
    if (isAvailable && pollPlan !== undefined) syncPoll(scope, pollPlan);
  }, [isAvailable, pollPlan, scope]);

  const reload = useCallback(() => {
    cache.delete(scope);
    stopPoll(scope);
    if (state) syncPoll(scope, brebKeyPollPlan(state.keys));
    setIsRefreshing(true);
    notify(scope);
  }, [scope, state]);

  const create = useCallback(
    async (source: BrebKeySource) => {
      const created = await BrebReceivingKeyService.create(source, scopedOrgId);
      const seeded = seedCreatedKeyState(state, created);
      setState(seeded);
      setHasError(false);
      refreshSeed(scope, scopedOrgId, seedState(scope, seeded));
      return created;
    },
    [scope, scopedOrgId, state],
  );

  // While other keys remain they stay on screen; without any, the options must be read before the wizard shows.
  const remove = useCallback(
    async (keyId: string | undefined, acceptLiveQrs = false, twoFactorCode?: string) => {
      try {
        await BrebReceivingKeyService.remove({
          organizationId: scopedOrgId,
          keyId,
          acceptLiveQrs,
          twoFactorCode,
        });
      } catch (error) {
        if (readApiStatus(error) !== 404) throw error;
      }
      const removedId = keyId ?? state?.key?.id;
      const remaining = state && removedId ? seedRemovedKeyState(state, removedId) : null;
      if (remaining && remaining.keys.length > 0) {
        setState(remaining);
        refreshSeed(scope, scopedOrgId, seedState(scope, remaining));
        return;
      }
      cache.delete(scope);
      stopPoll(scope);
      setState(null);
      setIsLoading(true);
      notify(scope);
    },
    [scope, scopedOrgId, state],
  );

  // A refused pin (the key was deleted or is not ACTIVE any more) means what is on screen is stale.
  const setPrimary = useCallback(
    async (keyId: string) => {
      let primary: BrebReceivingKey;
      try {
        primary = await BrebReceivingKeyService.setPrimary(keyId, scopedOrgId);
      } catch (error) {
        reload();
        throw error;
      }
      if (state) {
        const seeded = seedPrimaryKeyState(state, keyId);
        setState(seeded);
        refreshSeed(scope, scopedOrgId, seedState(scope, seeded));
      }
      return primary;
    },
    [reload, scope, scopedOrgId, state],
  );

  const notifyHolder = useCallback(
    (source: BrebKeySource) =>
      BrebReceivingKeyService.notifyHolder(source, scopedOrgId).then(
        (result) => result.notified,
        () => false,
      ),
    [scopedOrgId],
  );

  return {
    state,
    isLoading,
    isRefreshing,
    hasError,
    isAvailable,
    scopedOrgId,
    reload,
    create,
    remove,
    setPrimary,
    notifyHolder,
  };
}

export interface UseBrebKeyOptions {
  options: BrebKeyOption[];
  isLoading: boolean;
  hasError: boolean;
  reload: () => void;
}

// Asks the directory about every option still addable, so it reads only while the "add a key" step is open.
export function useBrebKeyOptions(scopedOrgId: string | undefined, active: boolean): UseBrebKeyOptions {
  const [options, setOptions] = useState<BrebKeyOption[]>([]);
  const [isLoading, setIsLoading] = useState(active);
  const [hasError, setHasError] = useState(false);
  const [version, setVersion] = useState(0);

  // Options read for an earlier "Add another key" may be taken by now; they never reach the next one.
  useEffect(() => {
    // eslint-disable-next-line react-hooks-eslint/set-state-in-effect
    if (!active) setOptions([]);
  }, [active]);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    /* eslint-disable react-hooks-eslint/set-state-in-effect */
    setIsLoading(true);
    setHasError(false);
    /* eslint-enable react-hooks-eslint/set-state-in-effect */
    BrebReceivingKeyService.getState(scopedOrgId, true)
      .then((state) => {
        if (!cancelled) setOptions(state.options);
      })
      .catch(() => {
        if (!cancelled) setHasError(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [active, scopedOrgId, version]);

  const reload = useCallback(() => setVersion((current) => current + 1), []);
  return { options, isLoading, hasError, reload };
}
